/**
 * QMS Assignment Prompt Dialog
 * Prompts user to optionally assign someone when moving a ticket to "In Progress"
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { QmsUserSelect } from './QmsUserSelect';
import type { QmsUser } from '../../types/qms.types';

export interface QmsAssignmentPromptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAssign: (userId: string) => void | Promise<void>;
  onSkip: () => void | Promise<void>;
  isLoading?: boolean;
}

export function QmsAssignmentPromptDialog({
  open,
  onOpenChange,
  onAssign,
  onSkip,
  isLoading = false,
}: QmsAssignmentPromptDialogProps) {
  const { t } = useTranslation('qms');
  const [selectedUser, setSelectedUser] = useState<QmsUser | undefined>(undefined);

  const handleAssign = async () => {
    if (!selectedUser?._id) return;
    try {
      await onAssign(selectedUser._id);
      setSelectedUser(undefined);
    } catch (error) {
      // Error is logged by the hook, show user-friendly message
      toast.error(t('errors.unexpectedError'));
    }
  };

  const handleSkip = async () => {
    try {
      await onSkip();
      setSelectedUser(undefined);
    } catch (error) {
      // Error is logged by the hook, show user-friendly message
      toast.error(t('errors.unexpectedError'));
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setSelectedUser(undefined);
    }
    onOpenChange(newOpen);
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('assignmentPrompt.title')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('assignmentPrompt.description')}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="py-4">
          <Label htmlFor="assignee-select" className="text-sm font-medium">
            {t('assignmentPrompt.selectAssignee')}
          </Label>
          <div className="mt-2">
            <QmsUserSelect
              value={selectedUser}
              onChange={(user) => setSelectedUser(user)}
              disabled={isLoading}
              placeholder={t('assignmentPrompt.selectAssignee')}
            />
          </div>
        </div>

        <AlertDialogFooter>
          <Button
            variant="outline"
            onClick={handleSkip}
            disabled={isLoading}
          >
            {t('assignmentPrompt.skip')}
          </Button>
          <Button
            onClick={handleAssign}
            disabled={isLoading || !selectedUser}
          >
            {isLoading ? t('assignmentPrompt.assigning') : t('assignmentPrompt.assignAndContinue')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
